/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';

// core
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core';
// icons
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  GetApp as DownloadIcon,
  ArrowBack as BackIcon,
  CloudDownload as BulkDownloadIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from '@material-ui/icons';
import { useTheme } from '@material-ui/core/styles';
import { getCompanyById } from '../../../requests/api/company';

// components
import useStyles from './styles';
import Snackbar from '../../../components/Snackbar';

const DocumentTickets = () => {
  const styles = useStyles();
  const { t } = useTranslation();
  // The folder / file type icons set their colour through inline `style` props.
  const theme = useTheme();

  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [currentPath, setCurrentPath] = useState('');
  const [downloadingFolder, setDownloadingFolder] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const tableHeaders = [
    { id: 'type', label: <b>Type</b> },
    { id: 'name', label: <b>Name</b> },
    { id: 'lastModified', label: <b>Last modified</b> },
    { id: 'size', label: <b>Size</b> },
    { id: 'actions', label: <b>Actions</b> },
  ];

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('accessToken');
      const companyId = localStorage.getItem('companyId');

      if (!token || !companyId) return;

      try {
        const response = await getCompanyById(companyId);
        if (response.success) {
          const decoded = jwtDecode(token);
          // Prioridad: API → token → fallback
          const company = response.data.data.company.name
            || decoded?.user?.company?.name
            || decoded?.user?.companyName
            || 'Distal';

          setCompanyName(company);
          loadS3Objects(company, '');
        }
      } catch (err) {
        console.error('💥 Error inicializando la app:', err);
      }
    };

    init();
  }, []);

  const loadS3Objects = async (company, path) => {
    setLoading(true);
    try {
      const baseUrl = 'https://s3.sa-east-1.amazonaws.com/tcg-public';
      const prefix = path ? `companies/${company}/${path}/` : `companies/${company}/`;
      const params = new URLSearchParams({
        'list-type': '2',
        prefix,
        delimiter: '/',
      });

      const response = await fetch(`${baseUrl}?${params.toString()}`);
      const xmlText = await response.text();
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

      // Check if folder exists by checking if there are any results
      const contents = xmlDoc.getElementsByTagName('Contents');
      const prefixes = xmlDoc.getElementsByTagName('CommonPrefixes');

      // If no contents and no prefixes and we're at root level, create the folder
      if (contents.length === 0 && prefixes.length === 0 && !path) {
        await createCompanyFolder(company);
      }

      // Get folders
      const folders = Array.from(prefixes).map((prefixElement) => {
        const key = prefixElement.getElementsByTagName('Prefix')[0].textContent;
        const folderName = key.replace(prefix, '').replace('/', '');
        return {
          key,
          name: folderName,
          isFolder: true,
          size: 0,
          lastModified: null,
        };
      }).filter((folder) => folder.name);

      // Get files
      const files = Array.from(contents)
        .filter((content) => {
          const key = content.getElementsByTagName('Key')[0].textContent;
          return key !== prefix && !key.endsWith('/') && key.startsWith(prefix);
        })
        .map((content) => {
          const key = content.getElementsByTagName('Key')[0].textContent;
          const size = content.getElementsByTagName('Size')[0].textContent;
          const lastModified = content.getElementsByTagName('LastModified')[0].textContent;
          const fileName = key.replace(prefix, '');
          return {
            key,
            name: fileName,
            size: parseInt(size),
            lastModified: new Date(lastModified),
            isFolder: false,
          };
        }).filter((file) => file.name && !file.name.includes('/'));

      setObjects([...folders, ...files]);
      setCurrentPath(path);
    } catch (error) {
      setErr('Error loading documents');
    }
    setLoading(false);
  };

  const createCompanyFolder = async (company) => {
    try {
      // Call backend API to create the folder
      const response = await fetch('/api/s3-browser/create-folder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ companyName: company }),
      });
    } catch (_) {
      return null;
    }
  };

  const handleFolderClick = (folderName) => {
    const newPath = currentPath ? `${currentPath}/${folderName}` : folderName;
    loadS3Objects(companyName, newPath);
  };

  const handleBackClick = () => {
    const pathParts = currentPath.split('/').filter(Boolean);
    pathParts.pop();
    const newPath = pathParts.join('/');
    loadS3Objects(companyName, newPath);
  };

  const handleDownload = (objectKey) => {
    const url = `https://tcg-public.s3.sa-east-1.amazonaws.com/${objectKey}`;
    window.open(url, '_blank');
  };

  const getAllFilesRecursively = async (company, path) => {
    const baseUrl = 'https://s3.sa-east-1.amazonaws.com/tcg-public';
    const prefix = path ? `companies/${company}/${path}/` : `companies/${company}/`;
    const params = new URLSearchParams({
      'list-type': '2',
      prefix,
    });

    const response = await fetch(`${baseUrl}?${params.toString()}`);
    const xmlText = await response.text();
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

    return Array.from(xmlDoc.getElementsByTagName('Contents'))
      .filter((content) => {
        const key = content.getElementsByTagName('Key')[0].textContent;
        return key !== prefix && !key.endsWith('/') && key.startsWith(prefix);
      })
      .map((content) => {
        const key = content.getElementsByTagName('Key')[0].textContent;
        const fileName = key.replace(`companies/${company}/`, '');
        return { key, name: fileName };
      });
  };

  const handleBulkDownload = async (folderPath = null) => {
    setDownloadingFolder(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      const pathToDownload = folderPath || currentPath;
      const allFiles = await getAllFilesRecursively(companyName, pathToDownload);

      if (allFiles.length === 0) {
        setErr('No hay archivos para descargar en esta carpeta');
        setDownloadingFolder(false);
        return;
      }

      const downloadPromises = allFiles.map(async (file) => {
        try {
          const response = await fetch(`https://tcg-public.s3.sa-east-1.amazonaws.com/${file.key}`);
          const blob = await response.blob();
          zip.file(file.name, blob);
        } catch (_) {
          return null;
        }
      });

      await Promise.all(downloadPromises);

      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const link = document.createElement('a');
      link.href = url;
      const folderName = pathToDownload ? pathToDownload.split('/').pop() : companyName;
      link.download = `${folderName}_documents.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErr('Error al crear el archivo ZIP');
    }
    setDownloadingFolder(false);
  };

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...files]);
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    setLoading(true);
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('companyName', companyName);
      formData.append('path', currentPath);

      const response = await fetch('/api/documentToS3', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: formData,
      });

      if (response.ok) {
        setUploadModalOpen(false);
        setSelectedFiles([]);
        // Reload the current folder to show new files
        loadS3Objects(companyName, currentPath);
      } else {
        setErr('Error uploading files');
      }
    } catch (error) {
      setErr('Error uploading files');
    }
    setLoading(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getBreadcrumbs = () => {
    if (!currentPath) return [];
    return currentPath.split('/').filter(Boolean);
  };

  const content = () => {
    if (!objects.length) {
      return (
        <TableRow>
          <TableCell colSpan={5} className={styles.users}>
            <FileIcon />
            <h4>{t('documents.noDocuments')}</h4>
          </TableCell>
        </TableRow>
      );
    }
    return (
      <>
        {objects.map((obj, index) => (
          <TableRow
            hover
            key={index}
            style={{ cursor: obj.isFolder ? 'pointer' : 'default' }}
            onClick={obj.isFolder ? () => handleFolderClick(obj.name) : undefined}
          >
            <TableCell component="th" scope="row" padding="none">
              {obj.isFolder ? (
                <FolderIcon style={{ verticalAlign: 'middle', marginRight: 8, color: theme.palette.custom.folderIcon }} />
              ) : (
                // '#666' is exactly `mutedText`. As a literal it was almost
                // invisible on a dark page.
                <FileIcon style={{ verticalAlign: 'middle', marginRight: 8, color: theme.palette.custom.mutedText }} />
              )}
            </TableCell>
            <TableCell align="left">{obj.name}</TableCell>
            <TableCell align="left">
              {obj.isFolder ? '-' : `${obj.lastModified.toLocaleDateString()} ${obj.lastModified.toLocaleTimeString()}`}
            </TableCell>
            <TableCell align="left">{formatFileSize(obj.size)}</TableCell>
            <TableCell align="center">
              {!obj.isFolder && (
                <IconButton onClick={() => handleDownload(obj.key)}>
                  <DownloadIcon />
                </IconButton>
              )}
              {obj.isFolder && (
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    const folderPath = currentPath ? `${currentPath}/${obj.name}` : obj.name;
                    handleBulkDownload(folderPath);
                  }}
                  title="Descargar carpeta completa"
                >
                  <BulkDownloadIcon />
                </IconButton>
              )}
            </TableCell>
          </TableRow>
        ))}
      </>
    );
  };

  return (
    <>
      <div className={styles.header}>
        <Box sx={{ width: '100%' }}>
          <Typography variant='h1' className={styles.title} gutterBottom>
            {t('documents.tableTitle')}
          </Typography>
          <Typography variant='h4' gutterBottom display="inline">
            Company: {companyName}
          </Typography>
          <Typography variant='subtitle1' className={styles.underlined} gutterBottom display="inline">
            {objects.length} items
          </Typography>
        </Box>
      </div>

      {/* Breadcrumb Navigation */}
      <Box mb={2} display="flex" alignItems="center" style={{ padding: '16px' }}>
        {currentPath && (
          <IconButton size="small" onClick={handleBackClick}>
            <BackIcon />
          </IconButton>
        )}
        <Breadcrumbs>
          <Link
            onClick={() => loadS3Objects(companyName, '')}
            style={{ cursor: 'pointer' }}
          >
            {companyName}
          </Link>
          {getBreadcrumbs().map((part, index) => (
            <Typography key={index} color="textPrimary">
              {part}
            </Typography>
          ))}
        </Breadcrumbs>

        {(objects.some((obj) => !obj.isFolder) || objects.some((obj) => obj.isFolder)) && (
          <IconButton
            onClick={() => handleBulkDownload()}
            disabled={downloadingFolder}
            title="Descargar todo el contenido de esta carpeta"
            style={{ marginLeft: 'auto' }}
          >
            {downloadingFolder ? <CircularProgress size={24} /> : <BulkDownloadIcon />}
          </IconButton>
        )}

        <IconButton
          onClick={() => setUploadModalOpen(true)}
          title="Subir archivos"
          style={{ marginLeft: 8 }}
        >
          <UploadIcon />
        </IconButton>
      </Box>

      <Paper className={styles.supportTable}>
        <TableContainer>
          <Table aria-labelledby="tableTitle" size="medium" aria-label="enhanced table">
            <TableHead>
              <TableRow>
                {tableHeaders.map((tableHeader) => (
                  <TableCell
                    key={tableHeader.id}
                    className={styles.tableHeader}
                    align={tableHeader.id === 'actions' ? 'center' : 'left'}
                    padding="normal"
                  >
                    {tableHeader.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className={styles.progress}>
                    <CircularProgress />
                    <h4>Loading...</h4>
                  </TableCell>
                </TableRow>
              ) : content()}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={err !== ''}
        message={err}
        severity="error"
        onClose={() => setErr('')}
      />

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Subir Archivos</DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <input
              type="file"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Seleccionar Archivos
              </Button>
            </label>
          </Box>

          {selectedFiles.length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Archivos seleccionados ({selectedFiles.length}):
              </Typography>
              <List>
                {selectedFiles.map((file, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={file.name}
                      secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton onClick={() => handleRemoveFile(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadModalOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleUploadFiles}
            variant="contained"
            color="primary"
            disabled={selectedFiles.length === 0}
          >
            Subir Archivos
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentTickets;
